import type * as React from "react";

import { forwardRef, memo } from "react";
import { cn } from "../../utils/classnames";
import { formatNumber } from "../../utils/format-number";
import { RatingStars } from "./RatingStars";
import { roundToNearestTenth } from "./round-to-nearest-tenth";

const DEFAULT_MINIMUM_RATING_FOR_DISPLAY = 0;

const STAR_COLOR_BY_VARIANT = {
  default: "default",
  gold: "gold",
  light: "white",
} as const;

interface RatingProps extends React.HTMLAttributes<HTMLDivElement> {
  minimumRating?: number;
  rating: number;
  ratingCount: number;
  service?: string;
  variant?: "default" | "gold" | "light";
  textClassname?: string;
}

/**
 * Rating component to display the rating and rating count.
 * The algorithm for showing the stars was inspired by Google and Amazon.
 */
const Rating = memo(
  forwardRef<HTMLDivElement, RatingProps>(
    (
      {
        className,
        minimumRating = DEFAULT_MINIMUM_RATING_FOR_DISPLAY,
        rating,
        ratingCount,
        service = "Google",
        textClassname,
        variant = "default",
        ...props
      },
      ref,
    ) => {
      const roundedRating = Math.max(
        0,
        Math.min(5, roundToNearestTenth(rating ?? 0)),
      );
      const formattedCount = formatNumber(ratingCount);

      if (roundedRating < minimumRating) {
        return null;
      }

      return (
        <div
          className={cn(
            "flex flex-row items-center gap-1",
            { "text-white": variant === "light" },
            className,
          )}
          {...props}
          aria-label={`Rating: ${roundedRating} out of 5 on ${service} with ${formattedCount} reviews`}
          ref={ref}
        >
          <RatingStars
            color={STAR_COLOR_BY_VARIANT[variant]}
            rating={roundedRating}
          />
          <div className={cn("ml-2 text-sm", textClassname)}>
            {roundedRating} star rating on {service} ({formattedCount} reviews)
          </div>
        </div>
      );
    },
  ),
);

Rating.displayName = "Rating";

export { Rating };
